'use client';

import { VoteType } from '@prisma/client';
import { motion, useAnimation } from 'framer-motion';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useSession } from 'next-auth/react';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';

// ✅ Больше не принимаем массив votes!
type VoteButtonsProps = {
  likes: number;
  dislikes: number;
  myVote: VoteType | null; // ✅ Прямой статус
  onVote: (voteType: VoteType) => void;
};

export const VoteButtons = memo(function VoteButtons ({ likes, dislikes, myVote, onVote }: VoteButtonsProps) {
  const { data: session } = useSession();
  const userId = session?.user?.id;

  // 🎯 Анимации
  const likeControls = useAnimation();
  const dislikeControls = useAnimation();
  const prevVote = useRef<VoteType | null>(null);

  useEffect(() => {
    if (myVote === 'LIKE' && prevVote.current !== 'LIKE') {
      likeControls.start({ scale: [1, 1.4, 1], transition: { duration: 0.3 } });
    }
    if (myVote === 'DISLIKE' && prevVote.current !== 'DISLIKE') {
      dislikeControls.start({ scale: [1, 1.4, 1], transition: { duration: 0.3 } });
    }
    prevVote.current = myVote;
  }, [myVote, likeControls, dislikeControls]);

  const handleVote = useCallback(
    (voteType: VoteType) => {
      if (!userId) {
        toast.error('Нужно войти в аккаунт');
        return;
      }
      // Просто вызываем колбэк родителя, вся оптимистичная логика там
      onVote(voteType);
    },
    [userId, onVote],
  );

  const tooltips = useMemo(() => {
    return {
      like: myVote === 'LIKE' ? 'Отменить подтверждение' : 'Подтвердить',
      dislike: myVote === 'DISLIKE' ? 'Отменить опровержение' : 'Опровергнуть',
    };
  }, [myVote]);

  return (
    <div className="flex items-center gap-1 bg-slate-900/80 backdrop-blur-sm p-1.5 rounded-full pointer-events-auto border border-white/10">
      {/* LIKE */}
      <Tooltip>
        <TooltipTrigger asChild>
          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              handleVote('LIKE');
            }}
            whileTap={{ scale: 0.9 }}
            className={cn(
              'flex items-center gap-2 px-3 py-1 rounded-full transition-colors',
              myVote === 'LIKE' ? 'bg-sky-500/20' : 'hover:bg-white/10',
            )}
          >
            <motion.div animate={likeControls}>
              <ThumbsUp
                size={18}
                className={cn(
                  'transition-colors',
                  myVote === 'LIKE' ? 'text-sky-400' : 'text-slate-400',
                )}
                fill={myVote === 'LIKE' ? 'currentColor' : 'none'}
              />
            </motion.div>
            <span
              className={cn(
                'text-sm font-bold',
                myVote === 'LIKE' ? 'text-sky-100' : 'text-slate-300',
              )}
            >
              {likes}
            </span>
          </motion.button>
        </TooltipTrigger>
        <TooltipContent>{tooltips.like}</TooltipContent>
      </Tooltip>

      <div className="w-px h-4 bg-white/20 mx-0.5" />

      {/* DISLIKE */}
      <Tooltip>
        <TooltipTrigger asChild>
          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              handleVote('DISLIKE');
            }}
            whileTap={{ scale: 0.9 }}
            className={cn(
              'flex items-center gap-2 px-3 py-1 rounded-full transition-colors',
              myVote === 'DISLIKE' ? 'bg-rose-500/20' : 'hover:bg-white/10',
            )}
          >
            <motion.div animate={dislikeControls}>
              <ThumbsDown
                size={18}
                className={cn(
                  'transition-colors',
                  myVote === 'DISLIKE' ? 'text-rose-400' : 'text-slate-400',
                )}
                fill={myVote === 'DISLIKE' ? 'currentColor' : 'none'}
              />
            </motion.div>
            <span
              className={cn(
                'text-sm font-bold',
                myVote === 'DISLIKE' ? 'text-rose-100' : 'text-slate-300',
              )}
            >
              {dislikes}
            </span>
          </motion.button>
        </TooltipTrigger>
        <TooltipContent>{tooltips.dislike}</TooltipContent>
      </Tooltip>
    </div>
  );
})
